/* eslint-disable no-alert */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { EyeIcon, PencilIcon, XIcon } from "@heroicons/react/outline";
import { CheckIcon } from "@heroicons/react/solid";
import { useHistory } from "react-router-dom";
import Table from "components/lib/Global/ReactTable";
import TableActionMenu from "components/lib/Shared/TableActionMenu";
import CommentsPopup from "components/lib/Global/CommentsPopup";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { resolveFields, invalidateFields } from "state/slices/resolveField";
import { roundNumber } from "utils";
import { getComments } from "state/slices/comment";
import { deleteTemplateAsync } from "state/slices/template";
import {
  deleteShareAuthorizationAsync,
  resetAuthorizationSharesTable,
  getShareAuthorizationsAsync,
} from "state/slices/shareCapitalAuthorization";
import { submitAuthorizationsAsync } from "state/slices/shareCapitalAuthorization";
import {
  getAllInitiateTransactionChangeAsync,
  deleteInitiatedTransactionChangeAsync,
} from "state/slices/initiateTransactionChange";
import TableSubRow from "./TableSubRow";
import ProposedSubrow from "./ProposedSubrow";
import { useToast } from "hooks/useToast";
import { PlusIcon } from "@heroicons/react/solid";
import Button from "components/lib/Shared/Button";

const processTypeToChangeType = {
  authorization: "AUTHORIZATION",
  privateWithMoreThanOneClass: "ALLOTMENT",
  PVTwithOneClass: "ALLOTMENT",
  unallotedShares: "ALLOTMENT",
  shareTransfer: "SHARE-TRANSFER",
  transmissionOfShares: "TRANSMISSION-OF-SHARES",
  forfeitureOfShares: "FORFEITURE-OF-SHARES",
  surrenderOfShares: "SURRENDER-OF-SHARES",
  sharesBuyback: "SHARES-BUYBACK",
  cancelationOfTreasuryShares: "CANCELATION-OF-TREASURY-SHARES",
  saleTransferOfTreasuryShares: "SALE-TRANSFER-OF-TREASURY-SHARES",
  sharesRedemption: "SHARES-REDEMPTION",
  noticeToCourt: "NOTICE-TO-COURT",
  takeoverOffer: "TAKEOVER-OFFER",
  sharesConsolidation: "SHARES-CONSOLIDATION",
  sharesSubdivision: "SHARES-SUBDIVISION",
  noticeOfChangeOfName: "NOTICE-OF-CHANGE-OF-NAME",
  noticeOfParticulars: "NOTICE-OF-PARTICULARS",
  solvencyStatement: "SOLVENCY-STATEMENT",
  courtOrder: "COURT-ORDER",
  debenturesChargesRegistration: "DEBENTURES-CHARGES-REGISTRATION",
  debenturesChargesAmendment: "DEBENTURES-CHARGES-AMENDMENT",
};

const changeTypeToTitle = {
  authorization: "Authorization",
  privateWithMoreThanOneClass: "Allotment",
  PVTwithOneClass: "Allotment",
  unallotedShares: "Allotment",
  shareTransfer: "Share Transfer",
  transmissionOfShares: "Transmission of Shares",
  forfeitureOfShares: "Forfeiture of Shares",
  surrenderOfShares: "Surrender of Shares",
  sharesBuyback: "Shares Buyback",
  cancelationOfTreasuryShares: "Cancelation of Treasury Shares",
  saleTransferOfTreasuryShares: "Sale/Transfer of Treasury Shares",
  sharesRedemption: "Shares Redemption",
  noticeToCourt: "Notice to Court",
  takeoverOffer: "Takeover Offer",
  sharesConsolidation: "Share Consolidation",
  sharesSubdivision: "Share Sub-division",
  noticeOfChangeOfName: "Change of Name or Designation of Shares",
  noticeOfParticulars: "Particulars of Variation of Class Rights",
  solvencyStatement: "Solvency Statement",
  courtOrder: "Court Order",
  debenturesChargesRegistration: "Debentures/Charges Registration",
  debenturesChargesAmendment: "Debentures/Charges Amendment",
};

const ProposedSharesTable = (props) => {
  const { toast } = useToast(5000);
  const dispatch = useDispatch();
  const history = useHistory();
  const currentUser = useSelector((s) => s.userSlice);
  const { transactionUnderReview } = useSelector((s) => s.transactionSlice);
  const { transactions } = transactionUnderReview.items;
  let resolvedFields = [];
  if (transactions && transactions?.length > 0) {
    transactions.forEach((transaction) => {
      if (
        transaction?.resolvedFields &&
        transaction?.resolvedFields?.length > 0
      ) {
        resolvedFields = [...resolvedFields, ...transaction.resolvedFields];
      }
    });
  }

  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRow] = useState([]);
  const [allFieldsResolved, setAllFieldsResolved] = useState(false);
  const [expandedRow, setExpandedRow] = useState({});
  const [initialRows, setInitialRows] = useState({});

  const isViewMode = history.location.pathname.split("/").pop() === "view";
  const isReviewerMode =
    history.location.pathname.split("/").pop() === "review";

  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );
  const authorizations = useSelector(
    (state) => state.shareAuthorizationSlice.shares || []
  );

  // Nominal shares → votesPerShare lookups
  const nominalShares =
    useSelector(
      (state) => state.companyDashboardSlice?.company?.nominalShares
    ) || [];
  const votesPerShareByClass = useMemo(() => {
    const map = new Map();
    nominalShares.forEach((n) => {
      map.set(n.sharesCategory, Number(n.votesPerShare || 0));
    });
    return map;
  }, [nominalShares]);

  // Fetch transactions for non-authorization views
  useEffect(() => {
    if (props.companyId && props.changeType !== "authorization") {
      dispatch(
        getAllInitiateTransactionChangeAsync({
          companyId: props.companyId,
          params: { transactionType: "MANUAL", processType: props.changeType },
        })
      );
    }
  }, [props.companyId, dispatch, props.refreshTrigger, props.changeType]);

  // Normalizers
  const pickClass = (row) => {
    const cls =
      row?.classOfShares ||
      row?.sharesCategory ||
      row?.ProposalData?.sharesCategory ||
      row?.ProposalData?.classOfShares;
    if (!cls && process.env.NODE_ENV === "development") {
      console.warn("No valid share class found in row:", row);
    }
    return cls;
  };

  const pickSharesOnOffer = (row) => {
    const v =
      row?.sharesOnOffer ||
      row?.ProposalData?.sharesNumber ||
      row?.sharesNumber ||
      row?.newNumberOfShares;
    if (!v && process.env.NODE_ENV === "development") {
      console.warn("No valid shares number found in row:", row);
    }
    return Number(v) || 0;
  };

  const pickOfferPrice = (row) => {
    const v =
      row?.offerPricePerShare ??
      row?.ProposalData?.sharesValue ??
      row?.sharesValue ??
      row?.expectedShareValue;

    if (
      (v === undefined || v === null || v === "") &&
      process.env.NODE_ENV === "development"
    ) {
      console.warn("No valid share value found in row:", row);
    }
    return Number(v) || 0;
  };

  const pickVotesPerShare = (row, cls) => {
    const fromNominal = votesPerShareByClass.get(cls);
    if (typeof fromNominal === "number" && !Number.isNaN(fromNominal)) {
      return fromNominal;
    }
    const v = row?.votesPerShare || row?.ProposalData?.votesPerShare || 0;
    return Number(v);
  };

  // Build authorization rows helper (optimized for proposed prefilling)
  const buildAuthorizationRows = (filterApproved) => {
    const source = authorizations.filter((auth) => {
      // Filter by approval status
      const matchApproval =
        typeof filterApproved === "boolean"
          ? auth.isApproved === filterApproved
          : true;

      if (!matchApproval) return false;

      // Filter by current transaction context to avoid showing old processed data
      const currentTxIds = props.createdTransactionsIds || [];
      if (currentTxIds.length > 0) {
        // If an authorization has a trxId, it must be in the current wizard session's list
        return currentTxIds.includes(auth.trxId);
      }

      return true;
    });

    const rows = source.map((auth) => {
      const cls = auth.classOfShares || auth.sharesCategory || "";
      if (!cls) return null;

      // Direct mappings from authorization shape
      const sharesOnOffer = Number(
        auth.sharesOnOffer ?? auth.sharesNumber ?? 0
      );
      const offer = Number(auth.offerPricePerShare ?? auth.sharesValue ?? 0);
      const vps = Number(auth.votesPerShare ?? 0);

      const nominalValue = sharesOnOffer * offer;
      const votingRights = sharesOnOffer * vps;

      return {
        ...auth,
        _id: auth._id,
        trxId: auth.trxId || auth._id,
        transactionChangeType: "AUTHORIZATION",
        classOfShares: cls,
        sharesOnOffer,
        offerPricePerShare: offer,
        nominalValue,
        votingRights,
      };
    });
    return rows.filter(Boolean);
  };

  // Build proposed authorization rows for privateWithMoreThanOneClass
  const buildProposedAuthorizationRows = () => {
    if (props.changeType === "privateWithMoreThanOneClass") {
      // Only get unapproved authorizations
      return buildAuthorizationRows(false);
    }
    return [];
  };

  // Build proposed allotment rows from INITIATED TRANSACTION SLICE
  const buildAllotmentRowsFromTransactions = (targetProcessType) => {
    const txs = initiatedTransactions.items.filter(
      (tx) =>
        tx.transactionType === "MANUAL" &&
        tx.status !== "PROCESSED" &&
        (tx.processType === targetProcessType ||
          tx.changeType === processTypeToChangeType[targetProcessType])
    );

    let rows = [];

    for (const tx of txs) {
      // 1) Populated allotments
      if (
        Array.isArray(tx.allotmentsPopulated) &&
        tx.allotmentsPopulated.length
      ) {
        rows = [
          ...rows,
          ...tx.allotmentsPopulated.map((a) => {
            // Skip if it's null, undefined, or not an object
            if (!a || typeof a !== "object") {
              return null;
            }

            // Skip if it's an empty object with no useful properties
            const hasUsefulData = Object.keys(a).some((key) =>
              [
                "classOfShares",
                "sharesCategory",
                "sharesOnOffer",
                "sharesNumber",
                "offerPricePerShare",
                "sharesValue",
                "ProposalData",
              ].includes(key)
            );
            if (!hasUsefulData) {
              return null;
            }

            const cls = pickClass(a);
            if (!cls) {
              if (process.env.NODE_ENV === "development") {
                console.warn("Invalid allotment class, skipping:", a);
              }
              return null;
            }

            const sharesOnOffer = pickSharesOnOffer(a);
            if (sharesOnOffer === 0) {
              if (process.env.NODE_ENV === "development") {
                console.warn("Invalid allotment shares number, skipping:", a);
              }
              return null;
            }

            const offer = pickOfferPrice(a);
            if (offer === 0 && tx.processType !== "forfeitureOfShares") {
              if (process.env.NODE_ENV === "development") {
                console.warn("Invalid allotment share value, skipping:", a);
              }
              return null;
            }

            const nominalValue = sharesOnOffer * offer;
            const vps = pickVotesPerShare(a, cls);
            const votingRights = sharesOnOffer * vps;

            return {
              ...a,
              _id: a._id || tx._id,
              trxId: tx._id,
              transactionChangeType: tx.changeType,
              classOfShares: cls,
              sharesOnOffer,
              offerPricePerShare: offer,
              nominalValue,
              votingRights,
            };
          }),
        ];
      }

      // 2) Bare allotment IDs
      if (Array.isArray(tx.allotments) && tx.allotments.length) {
        rows = [
          ...rows,
          ...tx.allotments.map((idOrObj) => {
            // Skip if it's just a string ID without object data
            if (typeof idOrObj !== "object" || idOrObj === null) {
              return null;
            }

            // Skip if it's an empty object with no useful properties
            const hasUsefulData = Object.keys(idOrObj).some((key) =>
              [
                "classOfShares",
                "sharesCategory",
                "sharesOnOffer",
                "sharesNumber",
                "offerPricePerShare",
                "sharesValue",
                "ProposalData",
              ].includes(key)
            );
            if (!hasUsefulData) {
              return null;
            }

            const a = idOrObj;
            const cls = pickClass(a);
            if (!cls) {
              if (process.env.NODE_ENV === "development") {
                console.warn("Invalid allotment class, skipping:", a);
              }
              return null;
            }

            const sharesOnOffer = pickSharesOnOffer(a);
            if (sharesOnOffer === 0) {
              if (process.env.NODE_ENV === "development") {
                console.warn("Invalid allotment shares number, skipping:", a);
              }
              return null;
            }

            const offer = pickOfferPrice(a);
            if (offer === 0 && tx.processType !== "forfeitureOfShares") {
              if (process.env.NODE_ENV === "development") {
                console.warn("Invalid allotment share value, skipping:", a);
              }
              return null;
            }

            const nominalValue = sharesOnOffer * offer;
            const vps = pickVotesPerShare(a, cls);
            const votingRights = sharesOnOffer * vps;

            return {
              ...a,
              _id: a._id || tx._id,
              trxId: tx._id,
              transactionChangeType: tx.changeType,
              classOfShares: cls,
              sharesOnOffer,
              offerPricePerShare: offer,
              nominalValue,
              votingRights,
            };
          }),
        ];
      }

      // 3) Share capital changes fallback
      if (
        Array.isArray(tx.shareCapitalChanges) &&
        tx.shareCapitalChanges.length
      ) {
        rows = [
          ...rows,
          ...tx.shareCapitalChanges.map((change) => {
            // Skip if it's null, undefined, or not an object
            if (!change || typeof change !== "object") {
              return null;
            }

            // Skip if it's an empty object with no useful properties
            const hasUsefulData = Object.keys(change).some((key) =>
              [
                "classOfShares",
                "sharesCategory",
                "sharesOnOffer",
                "sharesNumber",
                "offerPricePerShare",
                "sharesValue",
                "ProposalData",
                "newNumberOfShares",
                "expectedShareValue",
              ].includes(key)
            );
            if (!hasUsefulData) {
              return null;
            }

            const cls = pickClass(change);
            if (!cls) {
              if (process.env.NODE_ENV === "development") {
                console.warn(
                  "Invalid share capital change class, skipping:",
                  change
                );
              }
              return null;
            }

            const sharesOnOffer = pickSharesOnOffer(change);
            if (sharesOnOffer === 0) {
              if (process.env.NODE_ENV === "development") {
                console.warn(
                  "Invalid share capital change shares number, skipping:",
                  change
                );
              }
              return null;
            }

            const offer = pickOfferPrice(change);
            if (offer === 0 && tx.processType !== "forfeitureOfShares") {
              if (process.env.NODE_ENV === "development") {
                console.warn(
                  "Invalid share capital change share value, skipping:",
                  change
                );
              }
              return null;
            }

            const nominalValue = sharesOnOffer * offer;
            const vps = pickVotesPerShare(change, cls);
            const votingRights = sharesOnOffer * vps;

            return {
              ...change,
              _id: change._id || tx._id,
              trxId: tx._id,
              transactionChangeType: tx.changeType,
              classOfShares: cls,
              sharesOnOffer,
              offerPricePerShare: offer,
              nominalValue,
              votingRights,
            };
          }),
        ];
      }
    }

    const filteredRows = rows.filter(Boolean);

    return filteredRows;
  };

  // Compose final rows using switch statement
  const dataSource = useMemo(() => {
    const type = props.changeType;
    let rows = [];

    switch (type) {
      case "authorization":
        rows = buildAuthorizationRows(false);
        break;

      case "privateWithMoreThanOneClass":
        // For privateWithMoreThanOneClass, only show allotment rows from transactions
        rows = buildAllotmentRowsFromTransactions(type);
        break;

      default:
        rows = buildAllotmentRowsFromTransactions(type);
        break;
    }

    return rows;
  }, [
    props.changeType,
    authorizations,
    initiatedTransactions.items,
    votesPerShareByClass,
  ]);

  // Proposed authorization rows for privateWithMoreThanOneClass
  const proposedAuthorizationRows = useMemo(() => {
    const rows = buildProposedAuthorizationRows();
    return rows;
  }, [props.changeType, authorizations, votesPerShareByClass]);

  const getAvailableShareClasses = useCallback(() => {
    const type = props.changeType;
    if (type === "privateWithMoreThanOneClass") {
      return (authorizations || [])
        .filter((auth) => auth.isApproved === false)
        .map((auth) => auth.classOfShares || auth.sharesCategory)
        .filter(Boolean);
    }
    if (type === "PVTwithOneClass" || type === "unallotedShares") {
      const approvedAuths = (authorizations || [])
        .filter((auth) => auth.isApproved === true)
        .map((auth) => auth.classOfShares || auth.sharesCategory)
        .filter(Boolean);

      if (approvedAuths.length > 0) {
        return approvedAuths;
      }

      return nominalShares.map((share) => share.sharesCategory).filter(Boolean);
    }
    if (type === "shareTransfer" || type === "forfeitureOfShares") {
      return nominalShares.map((share) => share.sharesCategory).filter(Boolean);
    }
    return [];
  }, [props.changeType, authorizations, nominalShares]);

  const getProposedShareClasses = useCallback(() => {
    return dataSource.map((row) => pickClass(row)).filter(Boolean);
  }, [dataSource, pickClass]);

  const unproposedClasses = useMemo(() => {
    const available = getAvailableShareClasses();
    const proposed = getProposedShareClasses();
    return available.filter((cls) => !proposed.includes(cls));
  }, [getAvailableShareClasses, getProposedShareClasses]);

  const showAddButton = useMemo(() => {
    const type = props.changeType;

    if (
      ![
        "privateWithMoreThanOneClass",
        "PVTwithOneClass",
        "unallotedShares",
        "shareTransfer",
        "forfeitureOfShares",
      ].includes(type)
    ) {
      return false;
    }

    if (type === "shareTransfer" || type === "forfeitureOfShares") {
      return unproposedClasses.length > 0;
    }

    const availableClasses = getAvailableShareClasses();
    return (
      availableClasses.length > 1 &&
      unproposedClasses.length > 0 &&
      dataSource.length > 0
    );
  }, [
    props.changeType,
    unproposedClasses,
    dataSource.length,
    getAvailableShareClasses,
  ]);

  useEffect(() => {
    if (props.onShowAddButtonChange) {
      props.onShowAddButtonChange(showAddButton);
    }
  }, [showAddButton, props.onShowAddButtonChange]);

  // Handler for "Add another share on offer" button
  const handleAddAnotherShareOnOffer = () => {
    if (props.onAddAnotherShareOnOffer) {
      props.onAddAnotherShareOnOffer(unproposedClasses, props.changeType);
    }
  };

  // ===== totals / acceptance state handling =====
  const fieldsName = dataSource.map((i) => i.classOfShares).filter(Boolean);
  const sumOfVotingRights = dataSource.reduce(
    (acc, item) => roundNumber(acc + Number(item.votingRights || 0)),
    0
  );

  // Totals for proposed authorization table
  const authFieldsName = proposedAuthorizationRows
    .map((i) => i.classOfShares)
    .filter(Boolean);
  const authSumOfVotingRights = proposedAuthorizationRows.reduce(
    (acc, item) => roundNumber(acc + Number(item.votingRights || 0)),
    0
  );

  useEffect(() => {
    if (resolvedFields?.length > 0) {
      setAllFieldsResolved(
        fieldsName.every((element) => resolvedFields?.includes(element))
      );
    }
  }, [fieldsName, resolvedFields]);

  // Data for proposed allotment table
  const data = [...dataSource];
  if (dataSource.length > 0) {
    data.push({
      id: "total",
      classOfShares: "Total",
      sharesOnOffer: "",
      offerPricePerShare: "",
      premiumPerShare: "",
      votingRights: sumOfVotingRights,
      trxRef: "",
      status: "",
    });
  }

  // Data for proposed authorization table
  const authData = [...proposedAuthorizationRows];
  if (proposedAuthorizationRows.length > 0) {
    authData.push({
      id: "total",
      classOfShares: "Total",
      sharesOnOffer: "",
      offerPricePerShare: "",
      premiumPerShare: "",
      votingRights: authSumOfVotingRights,
      trxRef: "",
      status: "",
    });
  }

  const closeSubRow = (row) => {
    setInitialRows({});
    setExpandedRow({});
    row.toggleRowExpanded();
    // Only reset mode if this is not a transaction update scenario
    const isTransactionUpdate =
      props.changeType === "privateWithMoreThanOneClass" ||
      props.changeType === "PVTwithOneClass" ||
      props.changeType === "unallotedShares" ||
      props.changeType === "shareTransfer" ||
      props.changeType === "forfeitureOfShares";

    if (!isTransactionUpdate) {
      // Reset mode to exit editing state and restore UI for non-transaction scenarios
      props.setMode({ editing: false, id: "" });
    }
  };

  const handleUpdateTransaction = async (originalRow, updatedData) => {
    try {
      // Import the update logic from addSharesOnOfferForm
      const {
        handleCompanyMaintenanceFormData,
      } = require("utils/transactionRequestHandler");

      // Find the transaction to update
      const txs = initiatedTransactions.items.filter(
        (tx) =>
          tx.transactionType === "MANUAL" &&
          (tx.processType === props.changeType ||
            tx.changeType === processTypeToChangeType[props.changeType])
      );

      const targetTx = txs.find((tx) => tx._id === originalRow.trxId);

      if (!targetTx) {
        throw new Error("Transaction not found");
      }

      // Update the share capital change in the transaction
      const updatedShareCapitalChanges = targetTx.shareCapitalChanges.map(
        (change) => {
          if (change.classOfShares === originalRow.classOfShares) {
            return {
              ...change,
              ...updatedData,
              sharesOnOffer: Number(updatedData.sharesOnOffer),
              offerPricePerShare: Number(updatedData.offerPricePerShare || 0),
              premiumPerShare: Number(updatedData.premiumPerShare || 0),
              dateOfOffer: updatedData.dateOfOffer || null,
            };
          }
          return change;
        }
      );

      const updatePayload = {
        companyId: props.companyId,
        documentId: targetTx._id,
        formName: "initiateTransactionChange",
        changeType: processTypeToChangeType[props.changeType],
        processType: targetTx.processType,
        transactionType: "MANUAL",
        applicationType: "Share Capital",
        shareCapitalChanges: updatedShareCapitalChanges,
      };

      const txUpdate = await handleCompanyMaintenanceFormData(
        "existing-request",
        updatePayload,
        props.companyId
      );

      if (
        txUpdate.type !==
        "transactionChange/editInitiatedTransactionChangeAsync/fulfilled"
      ) {
        const msg =
          txUpdate?.payload?.messageText || "Failed to update transaction";
        throw new Error(msg);
      }

      // Refresh the transactions
      await dispatch(
        getAllInitiateTransactionChangeAsync({
          companyId: props.companyId,
          params: {
            transactionType: "MANUAL",
            processType: props.changeType,
          },
        })
      );

      return txUpdate.payload;
    } catch (error) {
      throw error;
    }
  };

  const renderRowSubComponent = useCallback(
    ({ row, rowProps, visibleColumns }) => {
      // Check if this row is from the proposed authorization table (for privateWithMoreThanOneClass)
      const isProposedAuthorizationRow =
        props.changeType === "privateWithMoreThanOneClass" &&
        row.original?.transactionChangeType === "AUTHORIZATION";

      // For transaction-based change types, use ProposedSubrow EXCEPT for proposed authorization rows
      if (
        (props.changeType === "privateWithMoreThanOneClass" ||
          props.changeType === "PVTwithOneClass" ||
          props.changeType === "existingAuth" ||
          props.changeType === "unallotedShares" ||
          props.changeType === "shareTransfer" ||
          props.changeType === "forfeitureOfShares") &&
        !isProposedAuthorizationRow
      ) {
        return (
          <ProposedSubrow
            row={row}
            changesMode={props.changeType}
            rowProps={rowProps}
            visibleColumns={visibleColumns}
            closeSubRow={() => closeSubRow(row)}
            companyId={props.companyId}
            onUpdateTransaction={handleUpdateTransaction}
          />
        );
      }

      // For authorization or proposed authorization rows, normalize fields so the subrow pre-fills correctly and mirrors NominalSharesTable behavior
      const normalizedForAuth = dataSource.map((a) => ({
        ...a,
        sharesCategory: a.classOfShares ?? a.sharesCategory,
        sharesNumber: a.sharesOnOffer ?? a.sharesNumber,
        sharesValue: a.offerPricePerShare ?? a.sharesValue,
      }));

      // For proposed authorization rows, use the authorization data instead
      const normalizedForProposedAuth = proposedAuthorizationRows.map((a) => ({
        ...a,
        sharesCategory: a.classOfShares ?? a.sharesCategory,
        sharesNumber: a.sharesOnOffer ?? a.sharesNumber,
        sharesValue: a.offerPricePerShare ?? a.sharesValue,
      }));

      return (
        <TableSubRow
          row={row}
          changeType={
            isProposedAuthorizationRow ? "authorization" : props.changeType
          }
          filteredOfficials={props.filteredOfficials}
          companyId={props.companyId}
          approvedShareAuthorizations={[]}
          mode={props.mode}
          rowProps={rowProps}
          visibleColumns={visibleColumns}
          initialRows={initialRows}
          takenShares={props.takenShares}
          closeSubRow={() => closeSubRow(row)}
          nominalShares={
            isProposedAuthorizationRow
              ? normalizedForProposedAuth
              : props.changeType === "authorization"
              ? normalizedForAuth
              : dataSource
          }
          isProposed={true}
          onRefresh={props.onRefresh}
        />
      );
    },
    [
      initialRows,
      props.changeType,
      props.mode,
      props.takenShares,
      props.filteredOfficials,
      dataSource,
      props.companyId,
      handleUpdateTransaction,
      props.onRefresh,
    ]
  );

  const handleEdit = (row, rest) => {
    if (!row.isExpanded) {
      row.toggleAllRowsExpanded(false);
      setInitialRows(rest.initialRows);
      setExpandedRow(row);
      row.toggleRowExpanded();
      props.setMode({ editing: true, id: row.original._id });
    } else {
      setInitialRows({});
      setExpandedRow({});
      row.toggleRowExpanded();
      // Only reset mode if this is not a transaction update scenario
      const isTransactionUpdate =
        props.changeType === "privateWithMoreThanOneClass" ||
        props.changeType === "PVTwithOneClass" ||
        props.changeType === "unallotedShares" ||
        props.changeType === "shareTransfer" ||
        props.changeType === "forfeitureOfShares";

      if (!isTransactionUpdate) {
        // Reset mode to exit editing state and restore UI for non-transaction scenarios
        props.setMode({ editing: false, id: "" });
      }
    }
  };

  const handleDelete = async (row) => {
    try {
      const isAuthContext =
        props.changeType === "authorization" ||
        row?.transactionChangeType === "AUTHORIZATION";

      if (isAuthContext) {
        const del = await dispatch(
          deleteShareAuthorizationAsync({ id: row._id })
        );
        dispatch(resetAuthorizationSharesTable());
        dispatch(getShareAuthorizationsAsync(props.companyId));
        if (deleteShareAuthorizationAsync.fulfilled.match(del)) {
          toast("success", "Authorization deleted successfully.");
          if (row.rightsAttached?._id) {
            await dispatch(
              deleteTemplateAsync({ templateId: row.rightsAttached._id })
            );
          }
          const trxId = row?.trxId || row?.transactionId;
          if (
            trxId &&
            trxId !== "undefined" &&
            typeof trxId === "string" &&
            trxId.length > 0
          ) {
            const fetch = await dispatch(
              getAllInitiateTransactionChangeAsync({
                companyId: props.companyId,
                params: {
                  transactionType: "MANUAL",
                  processType:
                    props.changeType === "authorization"
                      ? "authorization"
                      : props.changeType,
                },
              })
            );
            const txs = fetch?.payload?.items || [];
            const targetTx = txs.find((tx) => tx._id.toString() === trxId);
            if (targetTx) {
              const txDel = await dispatch(
                deleteInitiatedTransactionChangeAsync({
                  companyId: props.companyId,
                  initiateId: trxId,
                })
              );
              if (
                deleteInitiatedTransactionChangeAsync.fulfilled.match(txDel)
              ) {
                toast(
                  "success",
                  "Transaction deleted as the proposed authorization was removed."
                );
              } else {
                toast(
                  "error",
                  txDel.payload?.message || "Failed to delete transaction."
                );
              }
            } else {
              // If targetTx not found in fetch, we still try to delete it by ID if it looks valid
              await dispatch(
                deleteInitiatedTransactionChangeAsync({
                  companyId: props.companyId,
                  initiateId: trxId,
                })
              );
            }
          } else {
            toast("error", "Invalid transaction ID for authorization.");
          }
          try {
            await dispatch(
              submitAuthorizationsAsync({
                companyId: props.companyId,
                dateFields: { dateOfAuthorization: "", dateOfExpiry: "" },
                updateDates: true,
              })
            );
          } catch (_) {}
        } else {
          return;
        }
      } else {
        const transactionId = row.trxId || row._id;

        if (
          !transactionId ||
          transactionId === "undefined" ||
          typeof transactionId !== "string" ||
          transactionId.length === 0
        ) {
          toast(
            "error",
            "Cannot delete: Transaction ID is missing or invalid."
          );
          return;
        }
        const fetch = await dispatch(
          getAllInitiateTransactionChangeAsync({
            companyId: props.companyId,
            params: {
              transactionType: "MANUAL",
              processType: props.changeType,
            },
          })
        );
        const txs = fetch?.payload?.items || [];
        const targetTx = txs.find((tx) => tx._id.toString() === transactionId);
        if (!targetTx) {
          toast("error", "Transaction not found.");
          return;
        }
        const clearedShareCapitalChanges = (
          targetTx.shareCapitalChanges || []
        ).filter((change) => change.classOfShares !== row.classOfShares);

        if (clearedShareCapitalChanges.length === 0) {
          const del = await dispatch(
            deleteInitiatedTransactionChangeAsync({
              companyId: props.companyId,
              initiateId: transactionId,
            })
          );
          if (deleteInitiatedTransactionChangeAsync.fulfilled.match(del)) {
            toast("success", "Transaction deleted as no more details left.");
            dispatch(
              getAllInitiateTransactionChangeAsync({
                companyId: props.companyId,
                params: {
                  transactionType: "MANUAL",
                  processType: props.changeType,
                },
              })
            );
          } else {
            toast(
              "error",
              del.payload?.message || "Failed to delete transaction."
            );
          }
        } else {
          const {
            handleCompanyMaintenanceFormData,
          } = require("utils/transactionRequestHandler");
          let changeType;
          if (props.changeType === "privateWithMoreThanOneClass") {
            changeType = "AUTHORIZATION-AND-ALLOTMENT";
          } else {
            changeType =
              processTypeToChangeType[props.changeType] || "ALLOTMENT";
          }
          const updatePayload = {
            companyId: props.companyId,
            documentId: targetTx._id.toString(),
            formName: "initiateTransactionChange",
            changeType: changeType,
            processType: props.changeType,
            transactionType: "MANUAL",
            applicationType: "Share Capital",
            shareCapitalChanges: clearedShareCapitalChanges,
          };
          const txUpdate = await handleCompanyMaintenanceFormData(
            "existing-request",
            updatePayload,
            props.companyId
          );
          if (
            txUpdate.type ===
            "transactionChange/editInitiatedTransactionChangeAsync/fulfilled"
          ) {
            toast("success", "Transaction updated successfully.");
            await dispatch(
              getAllInitiateTransactionChangeAsync({
                companyId: props.companyId,
                params: {
                  transactionType: "MANUAL",
                  processType: props.changeType,
                },
              })
            );
          } else {
            const msg =
              txUpdate?.payload?.messageText || "Failed to update transaction.";
            toast("error", msg);
          }
        }
      }
    } catch (error) {
      console.error("Delete transaction error:", error);
      toast(
        "error",
        error?.message || "An unexpected error occurred during deletion."
      );
    }
  };

  const handleResolveBtnClicked = async (row) => {
    const fieldName = row.original.classOfShares;
    const commentsToBeResolved = props.comments?.filter(
      (c) => c.fieldName === fieldName && !c.isResolved
    );
    const ids = commentsToBeResolved?.map(({ _id }) => _id) ?? [];
    const sliceIndex = transactions.findIndex(
      (trx) => trx._id === row.original.trxId
    );
    setIsLoading(true);
    setSelectedRow((prev) => [...prev, fieldName]);
    const promise = await dispatch(
      resolveFields({
        data: {
          formId: row.original.trxId,
          formName: "initiateTransactionChange",
          fieldName,
          resolvedFields: [row.original._id],
          commentsToBeResolvedIds: ids,
        },
        updateEffectKey: "updateTransactionUnderReview",
        updateEffectPath: `transactionSlice.transactionUnderReview.items.transactions.${sliceIndex}`,
      })
    );
    if (ids.length > 0) {
      const query = { companyId: commentsToBeResolved[0]?.company };
      if (isReviewerMode) query.createdBy = currentUser?._id;
      await dispatch(getComments(query));
    }
    setIsLoading(false);
    setSelectedRow((prev) =>
      prev.filter((n) => n !== promise.meta.arg.data.fieldName)
    );
  };

  const handleInvalidateBtnClicked = async (row) => {
    const fieldName = row.original.classOfShares;
    const sliceIndex = transactions.findIndex(
      (trx) => trx._id === row.original.trxId
    );
    setIsLoading(true);
    setSelectedRow((prev) => [...prev, fieldName]);
    const promise = await dispatch(
      invalidateFields({
        data: {
          formId: row.original.trxId,
          formName: "initiateTransactionChange",
          fieldName,
          invalidFields: [row.original._id],
        },
        updateEffectKey: "updateTransactionUnderReview",
        updateEffectPath: `transactionSlice.transactionUnderReview.items.transactions.${sliceIndex}`,
      })
    );
    setIsLoading(false);
    setSelectedRow((prev) =>
      prev.filter((n) => n !== promise.meta.arg.data.fieldName)
    );
  };

  const columns = [
    { Header: "Share Category", accessor: "classOfShares" },
    {
      Header: "Number of Shares",
      accessor: "sharesOnOffer",
      Cell: ({ row }) => {
        const isReorg =
          row.original.transactionChangeType === "SHARES-CONSOLIDATION" ||
          row.original.transactionChangeType === "SHARES-SUBDIVISION";
        return isReorg
          ? row.original.newNumberOfShares
          : row.original.sharesOnOffer;
      },
    },
    {
      Header: "Value of Shares",
      accessor: "offerPricePerShare",
      Cell: ({ row }) => {
        if (row.original.id === "total") return "";

        const isReorg =
          row.original.transactionChangeType === "SHARES-CONSOLIDATION" ||
          row.original.transactionChangeType === "SHARES-SUBDIVISION";

        if (isReorg) {
          return row.original.expectedShareValue;
        }

        const offerPrice = Number(row.original.offerPricePerShare || 0);
        const premium = Number(row.original.premiumPerShare || 0);
        const actualShareValue = offerPrice - premium;

        return actualShareValue;
      },
    },
    {
      Header: "Nominal Value",
      accessor: "nominalValue",
      Cell: ({ row }) => {
        if (row.original.id === "total") return "";

        const isReorg =
          row.original.transactionChangeType === "SHARES-CONSOLIDATION" ||
          row.original.transactionChangeType === "SHARES-SUBDIVISION";

        if (isReorg) {
          return row.original.nominalValue;
        }

        const sharesCount = Number(row.original.sharesOnOffer || 0);
        const offerPrice = Number(row.original.offerPricePerShare || 0);
        const premium = Number(row.original.premiumPerShare || 0);
        const actualShareValue = offerPrice - premium;
        const nominalValue = sharesCount * actualShareValue;

        return nominalValue;
      },
    },
    {
      Header: "Voting Rights",
      accessor: "votingRights",
      Cell: ({ row }) => {
        if (row.original.id === "total") {
          return <span className="font-bold">{row.original.votingRights}</span>;
        }
        return row.original.votingRights;
      },
    },
  ];

  // Show actions for authorization (and auth table inside privateWithMoreThanOneClass) regardless of resolved state
  const isAuthContext =
    props.changeType === "authorization" ||
    (props.changeType === "privateWithMoreThanOneClass" && authData.length > 0);
  if (props.showActionColumn && (!allFieldsResolved || isAuthContext)) {
    columns.push({
      Header: "Actions",
      Cell: ({ row, ...rest }) => {
        if (row.original.id === "total") return "";
        return (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => props.handleView(row.original)}
              className="flex space-x-1 justify-center items-center focus:outline-none mr-5 hover:text-gray-600 py-2.5 text-sm leading-3 text-gray-500"
            >
              <EyeIcon className="w-6 h-6" />
              <span>Quick View</span>
            </button>
            {row?.isExpanded ? (
              <button onClick={() => handleEdit(row, rest)}>Close</button>
            ) : (
              <TableActionMenu
                row={row}
                handleEdit={() => handleEdit(row, rest)}
                handleDelete={() => handleDelete(row.original)}
              />
            )}
          </div>
        );
      },
    });
  }

  if (props.showAddCommentColumn && !isViewMode) {
    columns.push({
      Header: "Actions",
      Cell: ({ row }) => {
        if (row.original.id === "total") return "";
        const isRowLoading =
          isLoading && selectedRows.includes(row.original.classOfShares);
        return (
          <div className="flex items-center space-x-2">
            {!resolvedFields?.includes(row.original._id) && (
              <CommentsPopup
                handleSaveComment={props.handleSaveComment}
                formName="shareInformation"
                fieldName={row.original.classOfShares}
                fieldLabel={row.original.classOfShares}
                comment={props.comments.find(
                  (c) =>
                    c.fieldName === row.original.classOfShares &&
                    currentUser?._id === c.createdBy?._id
                )}
              >
                <span>Review</span>
                <PencilIcon className="inline w-4 h-4" />
              </CommentsPopup>
            )}
            <div className="flex items-center justify-center">
              {resolvedFields?.includes(row.original._id) ? (
                <button
                  className={`flex space-x-1 ${
                    isRowLoading ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                  onClick={() =>
                    !isRowLoading && handleInvalidateBtnClicked(row)
                  }
                >
                  {isRowLoading ? (
                    <SpinnerIcon className="text-primary" />
                  ) : (
                    <>
                      <span>Accepted </span>
                      <XIcon className="inline w-4 h-4" />
                    </>
                  )}
                </button>
              ) : (
                <button
                  className={`flex space-x-1 ${
                    isRowLoading ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                  onClick={() => !isRowLoading && handleResolveBtnClicked(row)}
                >
                  {isRowLoading ? (
                    <SpinnerIcon className="text-primary" />
                  ) : (
                    <>
                      <span>Accept</span>
                      <CheckIcon className="inline w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        );
      },
    });
  }

  // Dynamic title based on changeType and data source
  const title =
    props.changeType === "authorization"
      ? "Proposed Authorization"
      : `Proposed ${changeTypeToTitle[props.changeType] || "Change"}`;

  // Render proposed authorization table for privateWithMoreThanOneClass
  const renderAuthorizationTable = () => {
    if (
      props.changeType !== "privateWithMoreThanOneClass" ||
      proposedAuthorizationRows.length === 0 // Check if we have any proposed authorizations
    ) {
      return null;
    }
    return (
      <div className="bg-white w-full shadow mt-4">
        <h1 className="p-3 text-md leading-6 font-medium text-gray-900">
          Proposed Authorization
        </h1>
        <div className="w-full">
          <Table
            columns={columns}
            data={authData}
            renderRowSubComponent={renderRowSubComponent}
          />
        </div>
      </div>
    );
  };

  // Only render allotment table if there is data
  if (data.length === 0 && authData.length === 0) {
    return null;
  }

  return (
    <>
      {renderAuthorizationTable()}
      {data.length > 0 && (
        <>
          <div className="bg-white w-full shadow mt-4">
            <div className="flex justify-between items-center p-3">
              <h1 className="text-md leading-6 font-medium text-gray-900">
                {title}
              </h1>
              {showAddButton && (
                <Button
                  variant="text"
                  onClick={handleAddAnotherShareOnOffer}
                  className="flex items-center text-sm text-sflPrimary"
                >
                  <PlusIcon className="w-4 h-4 mr-1" aria-hidden="true" />
                  <span>Add another Class of Shares</span>
                </Button>
              )}
            </div>
            <div className="w-full">
              <Table
                columns={columns}
                data={data}
                renderRowSubComponent={renderRowSubComponent}
              />
            </div>
          </div>
          {/* Show additional shares form immediately after this table */}
          {props.showAdditionalSharesForm &&
            props.changeType === props.activeAdditionalFormChangeType && (
              <div className="relative mt-4">
                {props.isSyncingAfterUpdate && (
                  <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center rounded">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        Adding additional share…
                      </span>
                    </div>
                  </div>
                )}
                {props.additionalSharesFormComponent}
              </div>
            )}
        </>
      )}
    </>
  );
};

export default ProposedSharesTable;
